<template>
  <b-modal
    v-model="isVisible"
    title="Детали домашнего задания"
    size="lg"
    @hidden="resetData"
    hide-footer
  >
    <div v-if="homework" class="homework-details-modal">
      <!-- Header -->
      <div class="homework-header mb-4">
        <h4>{{ homework.homework_title }}</h4>
        <div class="d-flex align-items-center mt-2">
          <b-badge :variant="getStatusVariant(homework.status)" class="mr-2">
            {{ getStatusText(homework.status) }}
          </b-badge>
          <b-badge v-if="homework.grade" :variant="getGradeVariant(homework.grade)">
            {{ homework.grade }}
          </b-badge>
        </div>
      </div>

      <!-- Details Grid -->
      <div class="row mb-4">
        <div class="col-md-6">
          <h6>Информация о задании</h6>
          <table class="table table-sm table-borderless">
            <tr>
              <td><strong>Тип:</strong></td>
              <td>{{ getHomeworkTypeText(homework.homework_type) }}</td>
            </tr>
            <tr>
              <td><strong>Дата задания:</strong></td>
              <td>{{ formatDate(homework.assigned_at) }}</td>
            </tr>
            <tr>
              <td><strong>Срок сдачи:</strong></td>
              <td>{{ homework.due_at ? formatDate(homework.due_at) : 'Без срока' }}</td>
            </tr>
            <tr>
              <td><strong>Дата сдачи:</strong></td>
              <td>{{ homework.submitted_at ? formatDate(homework.submitted_at) : 'Не сдано' }}</td>
            </tr>
          </table>
        </div>
        <div class="col-md-6">
          <h6>Оценки</h6>
          <table class="table table-sm table-borderless">
            <tr>
              <td><strong>Балл:</strong></td>
              <td>
                {{ homework.score !== null ? homework.score : '-' }}/{{ homework.max_score || 100 }}
                <span v-if="homework.score !== null" class="text-muted">
                  ({{ Math.round((homework.score / homework.max_score) * 100) }}%)
                </span>
              </td>
            </tr>
            <tr>
              <td><strong>Оценка:</strong></td>
              <td>
                <b-badge v-if="homework.grade" :variant="getGradeVariant(homework.grade)">
                  {{ homework.grade }}
                </b-badge>
                <span v-else class="text-muted">Не оценено</span>
              </td>
            </tr>
            <tr>
              <td><strong>Статус:</strong></td>
              <td>
                <b-badge :variant="getStatusVariant(homework.status)">
                  {{ getStatusText(homework.status) }}
                </b-badge>
              </td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Teacher Feedback -->
      <div v-if="homework.teacher_feedback" class="mb-4">
        <h6>Отзыв преподавателя</h6>
        <div class="border rounded p-3 bg-light">
          {{ homework.teacher_feedback }}
        </div>
      </div>

      <!-- Submission Notes -->
      <div v-if="homework.submission_notes" class="mb-4">
        <h6>Заметки к сдаче</h6>
        <div class="border rounded p-3 bg-light">
          {{ homework.submission_notes }}
        </div>
      </div>

      <!-- Attachments -->
      <div v-if="homework.attachments && homework.attachments.length" class="mb-4">
        <h6>Вложения</h6>
        <div class="attachments-list">
          <div
            v-for="(attachment, index) in homework.attachments"
            :key="index"
            class="attachment-item d-flex align-items-center p-2 border rounded mb-2"
          >
            <feather-icon icon="FileIcon" size="16" class="mr-2" />
            <span>{{ attachment.name || `Вложение ${index + 1}` }}</span>
          </div>
        </div>
      </div>

      <!-- Close Button -->
      <div class="text-right">
        <b-button variant="secondary" @click="isVisible = false">
          Закрыть
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BBadge, BButton } from 'bootstrap-vue'

export default {
  name: 'HomeworkDetailsModal',
  components: { BModal, BBadge, BButton },
  props: {
    value: { type: Boolean, default: false },
    homework: { type: Object, default: null }
  },
  data() {
    return {
      isVisible: this.value
    }
  },
  watch: {
    value(newVal) { this.isVisible = newVal },
    isVisible(newVal) { this.$emit('input', newVal) }
  },
  methods: {
    getStatusVariant(status) {
      const variants = {
        assigned: 'primary', in_progress: 'info', submitted: 'warning',
        graded: 'success', late: 'danger', missing: 'secondary'
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        assigned: 'Назначено', in_progress: 'В работе', submitted: 'Сдано',
        graded: 'Оценено', late: 'Просрочено', missing: 'Не сдано'
      }
      return texts[status] || status
    },
    getGradeVariant(grade) {
      const excellent = ['A+', 'A', 'A-']
      const good = ['B+', 'B', 'B-']
      const satisfactory = ['C+', 'C', 'C-']

      if (excellent.includes(grade)) return 'success'
      if (good.includes(grade)) return 'primary'
      if (satisfactory.includes(grade)) return 'info'
      if (grade === 'D') return 'warning'
      return 'danger'
    },
    getHomeworkTypeText(type) {
      const types = {
        assignment: 'Задание', essay: 'Эссе', project: 'Проект', presentation: 'Презентация'
      }
      return types[type] || type
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      })
    },
    resetData() {
      // Reset any local state if needed
    }
  }
}
</script>

<style lang="scss" scoped>
.homework-details-modal {
  .homework-header {
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 1rem;
  }

  .bg-light {
    background-color: #f8f9fa !important;
  }

  .attachment-item {
    background-color: #f8f9fa;
  }
}

.dark-layout {
  .homework-details-modal {
    .homework-header {
      border-bottom-color: #404656;
    }

    .bg-light {
      background-color: #404656 !important;
      color: #d0d2d6;
    }

    .attachment-item {
      background-color: #404656;
      border-color: #555;
    }

    .table {
      color: #d0d2d6;
    }
  }
}
</style>